export type AiSearchTagMatch = {
  slug: string;
  label: string;
  confidence?: number;
  rationale?: string;
  count?: number | null;
};

export type PlanningAiSearchPlan = {
  query: string;
  matches: AiSearchTagMatch[];
  source: 'ai' | 'fallback' | string;
  model?: string | null;
  fallback_reason?: string | null;
  interpreted_intent?: string | null;
  location_query?: string | null;
  radius_km?: number | null;
  statuses?: string[];
  date_preset?: '7d' | '30d' | '90d' | '180d' | 'year' | null;
  residual_query?: string | null;
};

export type PlanningImpactSearchIntent = {
  residualQuery: string;
  impactMin: number | null;
  impactMax: number | null;
  label: string | null;
};

const IMPACT_SEARCH_PATTERNS: Array<{
  pattern: RegExp;
  impactMin: number;
  impactMax: number;
  label: string;
}> = [
  {
    pattern: /\b(?:major|large|high[ -]?impact)\s+(?:developments?|schemes?|applications?|proposals?)\b/gi,
    impactMin: 7,
    impactMax: 10,
    label: 'Major (scale 7-10)',
  },
  {
    pattern: /\b(?:medium|moderate|medium[ -]?impact)\s+(?:developments?|schemes?|applications?|proposals?)\b/gi,
    impactMin: 4,
    impactMax: 6,
    label: 'Medium (scale 4-6)',
  },
  {
    pattern: /\b(?:minor|small|low[ -]?impact)\s+(?:developments?|schemes?|applications?|proposals?)\b/gi,
    impactMin: 1,
    impactMax: 3,
    label: 'Low (scale 1-3)',
  },
];

export const parsePlanningImpactSearchIntent = (value: string): PlanningImpactSearchIntent => {
  let residualQuery = String(value || '').trim();
  for (const intent of IMPACT_SEARCH_PATTERNS) {
    intent.pattern.lastIndex = 0;
    if (!intent.pattern.test(residualQuery)) continue;
    intent.pattern.lastIndex = 0;
    residualQuery = residualQuery
      .replace(intent.pattern, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return {
      residualQuery,
      impactMin: intent.impactMin,
      impactMax: intent.impactMax,
      label: intent.label,
    };
  }
  return { residualQuery, impactMin: null, impactMax: null, label: null };
};

const FULL_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const POSTCODE_OUTCODE = /^[A-Z]{1,2}\d[A-Z\d]?$/i;
const PLANNING_REFERENCE = /^(?=.*\d)[A-Z0-9-]{0,12}\/?[A-Z0-9-]*\d[A-Z0-9-]*\/[A-Z0-9\/-]+$/i;
const NUMBERED_ADDRESS = /^\d+[A-Z]?\s+.+\b(?:street|road|avenue|lane|place|terrace|crescent|square|mews|wynd|close|row|way|walk|drive|park|grove|hill|green|gardens|court|circus|parade)\b.*$/i;

export const isPlanningReferenceQuery = (value: string): boolean => (
  PLANNING_REFERENCE.test(String(value || '').trim())
);

export const isDirectPlanningSearchQuery = (value: string): boolean => {
  const query = String(value || '').trim();
  if (!query) return false;
  return FULL_POSTCODE.test(query)
    || POSTCODE_OUTCODE.test(query)
    || isPlanningReferenceQuery(query)
    || NUMBERED_ADDRESS.test(query);
};

export const hasApplicablePlanningAiSearchPlan = (plan: PlanningAiSearchPlan): boolean => (
  (plan.matches || []).length > 0
  || Boolean(plan.location_query)
  || Boolean(plan.statuses?.length)
  || Boolean(plan.date_preset)
  || Boolean(plan.residual_query)
);

const DATE_LABELS: Record<string, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '180d': 'Last 6 months',
  year: 'Last year',
};

export const getPlanningAiSearchPlanLabels = (plan: PlanningAiSearchPlan): string[] => {
  const labels: string[] = [];
  (plan.matches || []).slice(0, 4).forEach((match) => {
    if (match.label && !labels.includes(match.label)) labels.push(match.label);
  });
  if (plan.location_query) labels.push(plan.location_query);
  (plan.statuses || []).forEach((status) => {
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    if (!labels.includes(label)) labels.push(label);
  });
  if (plan.date_preset && DATE_LABELS[plan.date_preset]) {
    labels.push(DATE_LABELS[plan.date_preset]);
  }
  if (plan.radius_km != null && Number.isFinite(Number(plan.radius_km))) {
    labels.push(`Within ${Number(plan.radius_km).toLocaleString('en-GB', { maximumFractionDigits: 1 })} km`);
  }
  return labels;
};
